import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Post, User } from 'src/app/core/models/core.models';

import { AppState } from 'src/app/reducers';
import { ConfirmDialogComponent } from 'src/app/core/components/popups/confirm-dialog/confirm-dialog.component';
import {
  DialogCloseOptions,
  DialogCloseReason,
  DialogService,
} from '@sui/dialog';
import { FeedPostViewMode } from 'src/app/core/services/feed.service';
import { LoggerService } from '@services/logger.service';
import { NomObjectClass } from 'src/app/core/constants';
import { ViewId, ViewPresetService } from '@services/view-preset.service';
import { PopoverBoundaryRegistry } from '@services/popover-boundary-registry.service';
import { PostService } from 'src/app/core/services/post.service';
import { PropertyViewConfig } from 'src/app/core/services/feed-settings.service';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { selectUser } from 'src/app/core/reducers';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap } from 'rxjs';

@Component({
  selector: 'app-story-posts',
  templateUrl: './story-posts.component.html',
  styleUrls: ['./story-posts.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class StoryPostsComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() postId: string;
  @Input() feedId: string;
  @Input() viewConfig: PropertyViewConfig[];

  user: User;

  // Story posts
  storyPosts: Post[] = [];
  storyPostsLoading = false;
  storyPostsTotalCount = 0;

  // Pagination
  storyPostsCurrentPage = 1;
  storyPostsItemsPerPage = 10;

  // Local view/sort state
  localViewConfig: PropertyViewConfig[] = [];
  localViewMode: FeedPostViewMode = 'default';
  timeShown: string = null;
  sortField = 'post_time_published';
  sortDirection: 'asc' | 'desc' = 'desc';
  searchQuery = '';

  // Projected toolbar dropdown data (local, persisted per feed as a view preset).
  viewItems: StoryMenuItem[] = [];
  /** `viewItems` split into runs so each radio run can sit in one sui-radio-group. */
  viewGroups: StoryMenuGroup[] = [];
  sortFieldItems: StoryMenuItem[] = [];
  sortOrderItems: StoryMenuItem[] = [];

  /** Feed id the pane preferences were last fetched for (one preset per feed). */
  private presetLoadedForFeedId: string | null = null;
  private presetSettings: StoryPostsPresetSettings | null = null;

  private destroyRef = inject(DestroyRef);
  private dialogService = inject(DialogService);
  private postService = inject(PostService);
  private logger = inject(LoggerService);
  private cdr = inject(ChangeDetectorRef);
  private boundaryRegistry = inject(PopoverBoundaryRegistry);
  private host = inject(ElementRef<HTMLElement>);
  private viewPresetService = inject(ViewPresetService);

  /** Nearest `[popoverBoundary]` pane (story details may render in a dialog) else `'body'`. */
  popoverContainer = signal<HTMLElement | string>('body');

  constructor(
    private store: Store<AppState>,
    private translateService: TranslateService,
  ) {
    this.store
      .select(selectUser)
      .pipe(
        tap((user) => {
          this.user = user;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.setupToolbar();
  }

  ngAfterViewInit(): void {
    this.popoverContainer.set(
      this.boundaryRegistry.findContainer(this.host.nativeElement) ?? 'body',
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['postId']) {
      this.setupToolbar();
    }
    if ((changes['postId'] || changes['feedId']) && this.postId) {
      this.ensurePresetThenLoad();
    }
  }

  /**
   * Loads the pane's per-feed view preset (once per feed), applies it, then
   * fetches the posts - so the first request already uses the saved sort.
   */
  private ensurePresetThenLoad(): void {
    if (!this.feedId || this.presetLoadedForFeedId === this.feedId) {
      this.loadStoryPosts();
      return;
    }
    this.viewPresetService
      .getViewPresetByContext(
        this.feedId,
        NomObjectClass.FEED,
        ViewId.PaneViewStoryPosts,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (preset) => {
          this.presetLoadedForFeedId = this.feedId;
          const settings = preset?.settings;
          this.presetSettings =
            settings &&
            typeof settings === 'object' &&
            Object.keys(settings).length > 0
              ? (settings as StoryPostsPresetSettings)
              : null;
          this.applyPresetSettings();
          this.loadStoryPosts();
          this.cdr.detectChanges();
        },
        error: () => {
          this.presetLoadedForFeedId = this.feedId;
          this.loadStoryPosts();
        },
      });
  }

  /** Overlays the saved per-feed preferences onto the input-derived state. */
  private applyPresetSettings(): void {
    const settings = this.presetSettings;
    if (!settings) {
      return;
    }
    if (Array.isArray(settings.view_config)) {
      this.localViewConfig = this.localViewConfig.map((option) => {
        const saved = settings.view_config.find(
          (v) => v.propertyUri === option.propertyUri,
        );
        return saved ? { ...option, selected: saved.selected } : option;
      });
    }
    if (settings.view_posts_mode) {
      this.localViewMode = settings.view_posts_mode;
    }
    const selectedTimeMode = this.localViewConfig.find(
      (c) => c.radioGroup === 'post_time_mode' && c.selected,
    );
    if (selectedTimeMode) {
      this.timeShown = selectedTimeMode.path as string;
    }
    if (settings.sort_field) {
      this.sortField = settings.sort_field;
    }
    if (settings.sort_direction) {
      this.sortDirection = settings.sort_direction;
    }
    this.buildViewItems();
  }

  /** Saves the pane's View/Sort preferences per feed (backend upserts by context). */
  private persistPreferences(): void {
    if (!this.feedId) {
      return;
    }
    this.presetSettings = {
      view_config: this.localViewConfig,
      view_posts_mode: this.localViewMode,
      sort_field: this.sortField,
      sort_direction: this.sortDirection,
    };
    this.viewPresetService
      .saveViewPreset(
        {
          view_id: ViewId.PaneViewStoryPosts,
          name: '',
          settings: this.presetSettings,
          context_object_id: this.feedId,
          context_object_class_id: NomObjectClass.FEED,
        },
        false,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  loadStoryPosts(): void {
    if (!this.postId) {
      this.logger.warn('Cannot load story posts without postId');
      return;
    }

    this.storyPostsLoading = true;
    this.postService
      .getStoryPosts(this.postId, {
        queryId: this.feedId,
        orderField: this.sortField,
        sortDirection: this.sortDirection,
        page: this.storyPostsCurrentPage,
        itemsPerPage: this.storyPostsItemsPerPage,
        searchQuery: this.searchQuery || undefined,
      })
      .pipe(
        tap((response) => {
          this.storyPosts = response.posts ?? [];
          this.storyPostsTotalCount = response.count_posts ?? 0;
          this.storyPostsLoading = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onStoryPostsPageChanged(page: number): void {
    this.storyPostsCurrentPage = page;
    this.loadStoryPosts();
  }

  onStoryPostsItemsPerPageChanged(itemsPerPage: number): void {
    this.storyPostsItemsPerPage = itemsPerPage;
    this.storyPostsCurrentPage = 1;
    this.loadStoryPosts();
  }

  removeStoryPost = (post: Post): void => {
    const message = this.translateService.instant(
      'post.confirm.are-you-sure-you-want-to-delete-this-item-from-the-feed',
    );

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: { message },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          this.postService
            .removeStoryMembers(this.postId, [post.id], this.feedId)
            .pipe(
              tap((response) => {
                this.storyPosts = response.posts ?? [];
                this.storyPostsTotalCount = response.count_posts ?? 0;
              }),
              takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
        }
      },
    );
  };

  private setupToolbar(): void {
    this.localViewConfig = this.viewConfig
      ? this.viewConfig.map((c) => ({ ...c }))
      : [];

    // Derive initial localViewMode from the config (the selected view_posts_mode radio item)
    const selectedViewMode = this.localViewConfig.find(
      (c) => c.radioGroup === 'view_posts_mode' && c.selected,
    );
    if (selectedViewMode) {
      this.localViewMode = selectedViewMode.path as FeedPostViewMode;
    }

    // Derive initial timeShown from the config (the selected post_time_mode radio item)
    const selectedTimeMode = this.localViewConfig.find(
      (c) => c.radioGroup === 'post_time_mode' && c.selected,
    );
    this.timeShown = selectedTimeMode
      ? (selectedTimeMode.path as string)
      : null;

    this.sortFieldItems = [
      {
        text: this.translateService.instant(
          'feed-actions.button.published-time',
        ),
        icon: ['fas', 'upload'],
        value: 'post_time_published',
      },
      {
        text: this.translateService.instant('feed-actions.button.created-time'),
        icon: ['fas', 'circle-plus'],
        value: 'post_time_created',
      },
      {
        text: this.translateService.instant(
          'feed-actions.button.potential-reach',
        ),
        icon: ['fab', 'chromecast'],
        value: 'post_reach',
      },
      {
        text: this.translateService.instant('feed-actions.button.content-rank'),
        icon: ['fas', 'chart-line'],
        value: 'post_rank',
      },
    ];
    this.sortOrderItems = [
      {
        text: this.translateService.instant('feed-actions.button.descending'),
        icon: ['fas', 'sort-amount-down'],
        value: 'desc',
      },
      {
        text: this.translateService.instant('feed-actions.button.ascending'),
        icon: ['fas', 'sort-amount-up'],
        value: 'asc',
      },
    ];

    this.buildViewItems();
  }

  private buildViewItems(): void {
    const createItems = (configItem: PropertyViewConfig): StoryMenuItem[] => {
      if (configItem.isTitle) {
        return [
          { isSeparator: true },
          { text: configItem.label, isTitle: true },
        ];
      }
      return [
        {
          text: configItem.label,
          icon: configItem.icon,
          // Radio rows need a value to bind a sui-radio-group against.
          value: String(configItem.path ?? configItem.label),
          isToggle: !configItem.isRadio,
          isRadio: configItem.isRadio,
          radioGroup: configItem.radioGroup,
          isSelected:
            configItem.radioGroup === 'view_posts_mode'
              ? configItem.path === this.localViewMode
              : configItem.selected,
          action: () => this.onViewConfigChange(configItem),
        },
      ];
    };

    const tools = this.localViewConfig.filter((c) =>
      c.visibility?.includes('tools'),
    );
    const properties = this.localViewConfig.filter(
      (c) => !c.visibility?.includes('tools'),
    );

    const items: StoryMenuItem[] = [
      ...tools.flatMap(createItems),
      {
        isTitle: true,
        text: this.translateService.instant('feed-actions.heading.metadata'),
      },
      ...properties.flatMap(createItems),
    ];

    // Drop a leading separator (a tools group emits one before its title).
    this.viewItems = items.filter(
      (item, index) => !(index === 0 && item.isSeparator),
    );
    this.viewGroups = this.groupViewItems(this.viewItems);
  }

  /** Collapses consecutive rows sharing a `radioGroup` into one group. */
  private groupViewItems(items: StoryMenuItem[]): StoryMenuGroup[] {
    const groups: StoryMenuGroup[] = [];

    for (const item of items) {
      const previous = groups[groups.length - 1];
      const runs =
        !!item.radioGroup && previous?.radioGroup === item.radioGroup;

      if (runs) {
        previous.items.push(item);
        if (item.isSelected) {
          previous.value = item.value;
        }
        continue;
      }

      groups.push({
        radioGroup: item.radioGroup,
        value: item.isSelected ? item.value : undefined,
        items: [item],
      });
    }

    return groups;
  }

  /** A radio run owns the whole group's selection; delegate to the picked row. */
  onViewRadioChange(group: StoryMenuGroup, value: unknown): void {
    group.items.find((item) => item.value === String(value))?.action?.();
  }

  private onViewConfigChange(configItem: PropertyViewConfig): void {
    if (configItem.isRadio) {
      if (configItem.radioGroup === 'view_posts_mode') {
        this.localViewMode = configItem.path as FeedPostViewMode;
      } else if (configItem.radioGroup === 'post_time_mode') {
        this.timeShown = configItem.path as string;
        this.localViewConfig = this.localViewConfig.map((opt) =>
          opt.radioGroup === 'post_time_mode'
            ? { ...opt, selected: opt.label === configItem.label }
            : opt,
        );
      }
    } else {
      this.localViewConfig = this.localViewConfig.map((item) =>
        item.path === configItem.path
          ? { ...item, selected: !item.selected }
          : item,
      );
    }
    this.buildViewItems();
    this.persistPreferences();
    this.cdr.detectChanges();
  }

  onSearchChanged = (query: string) => {
    this.searchQuery = query ?? '';
    this.storyPostsCurrentPage = 1;
    this.loadStoryPosts();
  };

  onSortFieldChange(value: string): void {
    this.sortField = value;
    this.storyPostsCurrentPage = 1;
    this.persistPreferences();
    this.loadStoryPosts();
  }

  onSortOrderChange(value: string): void {
    this.sortDirection = value as 'asc' | 'desc';
    this.storyPostsCurrentPage = 1;
    this.persistPreferences();
    this.loadStoryPosts();
  }
}

/** Persisted per-feed preferences of the story posts pane (view preset settings). */
interface StoryPostsPresetSettings {
  view_config?: PropertyViewConfig[];
  view_posts_mode?: FeedPostViewMode;
  sort_field?: string;
  sort_direction?: 'asc' | 'desc';
}

/** A menu row rendered inside the story-posts view/sort dropdowns. */
interface StoryMenuItem {
  text?: string;
  icon?: IconProp;
  value?: string;
  isSeparator?: boolean;
  isTitle?: boolean;
  isToggle?: boolean;
  isRadio?: boolean;
  isSelected?: boolean;
  radioGroup?: string;
  action?: () => void;
}

/**
 * A consecutive run of view rows. `radioGroup` set ⇒ the run is one mutually
 * exclusive group and renders inside a single `sui-radio-group`.
 */
interface StoryMenuGroup {
  radioGroup?: string;
  /** Currently selected `value` within a radio run. */
  value?: string;
  items: StoryMenuItem[];
}
